import { type UseQueryResult } from "@tanstack/react-query";
import classNames from "classnames";
import { motion } from "framer-motion";
import { FC } from "react";
import { BiCheck, BiCopy } from "react-icons/bi";
import { numericFormatter } from "react-number-format";
import { Tooltip } from "antd";
import styles from "./standard-token-card.module.scss";
import type { StandardTokenType } from "@/features/bridge/types/token-selector.types";
import { useStandardTokenManager } from "@/features/bridge/stores/standard-token-manager.store";
import StandardTokenImageLoader from "@/features/bridge/components/token-selector/selector-interface/standard-token/standard-token-image-loader";
import ImageFallbackPlaceholder from "@/features/bridge/components/token-selector/selector-interface/standard-token/image-fallback";
import StepperInput from "@/features/bridge/components/inputs/stepper-input";
import { useTokenEligibility, getEligibilityMessage } from "@/features/bridge/hooks/useTokenEligibility.hook";

type Props = {
  token: StandardTokenType;
  query: Omit<UseQueryResult<StandardTokenType[] | null, Error>, "refetch">;
};

const StandardTokenCard: FC<Props> = ({ token, query }: Props) => {
  const { selectedToken, setSelectedToken, setTokenAmount, tokenAmount } =
    useStandardTokenManager();

  const isActiveToken = token.tokenAddress === selectedToken?.tokenAddress;

  // Check token eligibility for bridging
  const eligibility = useTokenEligibility(token.tokenAddress);
  const eligibilityMessage = getEligibilityMessage(eligibility);

  // Format address to show first 10 and last 8 characters
  const formatAddress = (address: string): string => {
    if (address.length <= 18) return address;
    return `${address.slice(0, 10)}...${address.slice(-8)}`;
  };

  // Copy address to clipboard
  const copyToClipboard = async (address: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    try {
      await navigator.clipboard.writeText(address);
      // Optional: Add toast notification here
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  //? Currently inputted amount
  const inputtedTokenAmount = isActiveToken ? tokenAmount : 0;

  function handleValueChange(amount: number | undefined) {
    // Don't allow changes if token is not eligible
    if (!eligibility.isEligible) return;

    // Treat undefined (empty field) or <= 0 as unselecting the token
    if (amount === undefined || amount <= 0) {
      setSelectedToken(undefined);
      setTokenAmount(0); // Reset to 0
    } else if (amount > token.balance) {
      setTokenAmount(token.balance); // Clamp to the maximum balance
    } else {
      setSelectedToken(token);
      setTokenAmount(amount); // Accept valid input
    }
  }

  function handleSelectToken() {
    // Don't allow selection if token is not eligible
    if (!eligibility.isEligible) return;
    if (token.tokenAddress === selectedToken?.tokenAddress) return;
    setSelectedToken(token);
    setTokenAmount(0); // Don't set a default amount, let user input
  }

  function handleMaxAmount() {
    if (token.balance) {
      setTokenAmount(token.balance);
    }
  }

  return (
    <Tooltip
      title={!eligibility.isEligible ? eligibilityMessage : undefined}
      placement={'center' as any}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={classNames(
          styles.cardContainer,
          isActiveToken && styles.active,
          !eligibility.isEligible && styles.disabled,
        )}
        onClick={handleSelectToken}
      >
      <div className={styles.image}>
        {token.logoUrl ? (
          <StandardTokenImageLoader
            src={token.logoUrl}
            alt={token?.name + " display image"}
          />
        ) : (
          <ImageFallbackPlaceholder symbol={token.symbol} tokenAddress={token.tokenAddress} />
        )}
        <div
          className={classNames(styles.tick, isActiveToken && styles.active)}
        >
          <BiCheck size={25} />
        </div>
      </div>
      <div className={styles.text}>
        <p className={styles.cardTitle}>
          {token?.name} <span>({token?.symbol})</span>
        </p>
        <div className={styles.addressBalanceContainer}>
          <div className={styles.addressSection}>
              <span className={styles.address}>
                {formatAddress(token.tokenAddress)}
              </span>
            <button
              className={styles.copyButton}
              onClick={(e) => copyToClipboard(token.tokenAddress, e)}
              title="Copy address"
            >
              <BiCopy size={12} />
            </button>
          </div>
          <div className={styles.balanceSection}>
            <p
              className={classNames(
                styles.amount,
                !token.balance && styles.noAmount,
              )}
            >
              <b>
                {numericFormatter(token.balance.toString(), {
                  thousandSeparator: ",",
                  decimalScale: 4,
                })}
              </b>
            </p>
          </div>
        </div>
      </div>
      <StepperInput
        changeTokenAmount={(amount) => handleValueChange(amount)}
        incrementControls
        decrementToken={() => handleValueChange(inputtedTokenAmount - 1)}
        incrementToken={() => handleValueChange(inputtedTokenAmount + 1)}
        value={isActiveToken ? tokenAmount : ""}
        isLoading={query.isLoading || eligibility.isLoading}
        disabled={!isActiveToken || !eligibility.isEligible}
        fullDisabled={!token.balance || !eligibility.isEligible}
        handleMaxAmount={handleMaxAmount}
        maxValue={token.balance}
        isFocused={isActiveToken && eligibility.isEligible && tokenAmount === 0}
      />
    </motion.div>
    </Tooltip>
  );
};

export default StandardTokenCard;
